import React from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import HubRounded from '@mui/icons-material/HubRounded';
import { useFeature } from '../../ui/FeaturesProvider';
import { useCurrency } from '../../ui/BootstrapProvider';
import {
  ChainView,
  ChainViewSkeleton,
  CrossPivotPanel,
  EntityListPanel,
  RootCausePanel,
  RootCausePanelSkeleton,
  StageDetailStrip,
  fmtShortPeriodRange,
} from '../../components/chain-monitor';
import type { ChainStage } from '../../components/chain-monitor';
import {
  fetchBrokerMonitorBrokerDetail,
  fetchBrokerMonitorBrokers,
  fetchBrokerMonitorChain,
  fetchBrokerMonitorOperatorCrossBroker,
} from '../../services/brokerMonitorService';
import {
  brokersToEntities,
  toCrossPivotPanel,
  toRootCausePanel,
  type BMBrokerDetail,
  type BMBrokerList,
  type BMChainSnapshot,
  type BMOperatorCrossBroker,
} from '../../types/brokerMonitor';

export default function BrokerMonitorPage() {
  const feature = useFeature('broker_monitor');
  const { symbol: currencySymbol } = useCurrency();

  const [chain, setChain] = React.useState<BMChainSnapshot | null>(null);
  const [selectedAggStageId, setSelectedAggStageId] = React.useState<string | null>(null);
  const [brokerList, setBrokerList] = React.useState<BMBrokerList | null>(null);
  const [selectedBrokerId, setSelectedBrokerId] = React.useState<string | null>(null);
  const [brokerDetail, setBrokerDetail] = React.useState<BMBrokerDetail | null>(null);
  const [selectedStageId, setSelectedStageId] = React.useState<string | null>(null);
  const [crossBroker, setCrossBroker] = React.useState<BMOperatorCrossBroker | null>(null);
  const [crossBrokerLoading, setCrossBrokerLoading] = React.useState(false);

  const [loadingOverview, setLoadingOverview] = React.useState(true);
  const [loadingDetail, setLoadingDetail] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (feature.loading || !feature.enabled) return;
    let cancelled = false;
    setLoadingOverview(true);
    setError(null);
    Promise.all([fetchBrokerMonitorChain(), fetchBrokerMonitorBrokers()])
      .then(([chainRes, brokersRes]) => {
        if (cancelled) return;
        setChain(chainRes);
        setSelectedAggStageId(chainRes.stages[0]?.id ?? null);
        setBrokerList(brokersRes);
        const first = brokersRes.brokers.find((b) => b.sufficient_data) ?? brokersRes.brokers[0];
        if (first) setSelectedBrokerId(first.broker_id);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || 'Failed to load Broker Activity Monitor');
      })
      .finally(() => {
        if (!cancelled) setLoadingOverview(false);
      });
    return () => {
      cancelled = true;
    };
  }, [feature.loading, feature.enabled]);

  React.useEffect(() => {
    if (!selectedBrokerId) return;
    let cancelled = false;
    setLoadingDetail(true);
    setCrossBroker(null);
    fetchBrokerMonitorBrokerDetail(selectedBrokerId)
      .then((detail) => {
        if (cancelled) return;
        setBrokerDetail(detail);
        setSelectedStageId(detail.stages[0]?.id ?? null);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || `Failed to load broker ${selectedBrokerId}`);
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedBrokerId]);

  const handleViewCrossBroker = React.useCallback((operatorId: string) => {
    setCrossBrokerLoading(true);
    fetchBrokerMonitorOperatorCrossBroker(operatorId)
      .then(setCrossBroker)
      .catch((err: Error) => setError(err.message || `Failed to load operator ${operatorId}`))
      .finally(() => setCrossBrokerLoading(false));
  }, []);

  if (!feature.loading && !feature.enabled) {
    return (
      <Alert severity="info">
        Broker Activity Monitor is not enabled for this workspace. Enable it under{' '}
        <strong>Settings → Features</strong>.
      </Alert>
    );
  }

  if (loadingOverview) {
    return (
      <Stack spacing={2} sx={{ width: '100%' }}>
        <Paper variant="outlined" sx={(t) => ({ p: 2, borderRadius: `${t.ds?.radius?.md ?? 8}px` })}>
          <Skeleton variant="text" animation="wave" width="30%" height={28} sx={{ mb: 1.5 }} />
          <ChainViewSkeleton stageCount={5} />
        </Paper>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '280px 1fr' }, gap: 2 }}>
          <Skeleton variant="rounded" animation="wave" height={360} />
          <Skeleton variant="rounded" animation="wave" height={360} />
        </Box>
      </Stack>
    );
  }

  if (error && !chain) {
    return <Alert severity="error">{error}</Alert>;
  }

  const rangeLabel = chain ? fmtShortPeriodRange(chain.current_period.start, chain.current_period.end) : null;

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={(t) => ({ p: 2, mb: 2, borderRadius: `${t.ds?.radius?.md ?? 8}px` })}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Marketplace-wide chain — {chain?.comparison_label}
            {rangeLabel ? ` (${rangeLabel})` : ''}
          </Typography>
        </Stack>
        {chain && (
          <>
            <ChainView
              stages={chain.stages as ChainStage[]}
              selectedStageId={selectedAggStageId}
              onSelectStage={(id) => setSelectedAggStageId((prev) => (prev === id ? null : id))}
              currencySymbol={currencySymbol}
            />
            {selectedAggStageId && (
              <Box sx={{ mt: 1.5 }}>
                <StageDetailStrip
                  stage={chain.stages.find((s) => s.id === selectedAggStageId) as ChainStage}
                  comparisonLabel={chain.comparison_label}
                  currencySymbol={currencySymbol}
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '280px 1fr' }, gap: 2 }}>
        <Paper variant="outlined" sx={(t) => ({ p: 2, borderRadius: `${t.ds?.radius?.md ?? 8}px`, alignSelf: 'start' })}>
          <EntityListPanel
            title="Brokers"
            entities={brokerList ? brokersToEntities(brokerList.brokers) : []}
            selectedEntityId={selectedBrokerId}
            onSelectEntity={setSelectedBrokerId}
            maxVisibleItems={10}
          />
        </Paper>

        <Stack spacing={2}>
          {loadingDetail ? (
            <>
              <Paper variant="outlined" sx={(t) => ({ p: 2, borderRadius: `${t.ds?.radius?.md ?? 8}px` })}>
                <Skeleton variant="text" animation="wave" width="30%" height={28} sx={{ mb: 1.5 }} />
                <ChainViewSkeleton stageCount={5} />
              </Paper>
              <RootCausePanelSkeleton />
            </>
          ) : brokerDetail ? (
            <>
              <Paper variant="outlined" sx={(t) => ({ p: 2, borderRadius: `${t.ds?.radius?.md ?? 8}px` })}>
                <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {brokerDetail.broker_name} ({brokerDetail.broker_id})
                  </Typography>
                  {brokerDetail.market && (
                    <Typography variant="body2" color="text.secondary">
                      {brokerDetail.market}
                    </Typography>
                  )}
                </Stack>
                <ChainView
                  stages={brokerDetail.stages as ChainStage[]}
                  selectedStageId={selectedStageId}
                  onSelectStage={(id) => setSelectedStageId((prev) => (prev === id ? null : id))}
                  currencySymbol={currencySymbol}
                />
                {selectedStageId && (
                  <Box sx={{ mt: 1.5 }}>
                    <StageDetailStrip
                      stage={brokerDetail.stages.find((s) => s.id === selectedStageId) as ChainStage}
                      comparisonLabel={brokerDetail.comparison_label}
                      currencySymbol={currencySymbol}
                    />
                  </Box>
                )}
              </Paper>

              <RootCausePanel
                panel={toRootCausePanel(brokerDetail.root_cause)}
                actions={
                  brokerDetail.root_cause.cause === 'operator' && brokerDetail.top_operator ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<HubRounded fontSize="small" />}
                      onClick={() => handleViewCrossBroker(brokerDetail.top_operator!.operator_id)}
                    >
                      View cross-broker pivot for {brokerDetail.top_operator.operator_name}
                    </Button>
                  ) : undefined
                }
              />

              {crossBrokerLoading && <Skeleton variant="rounded" animation="wave" height={180} />}
              {crossBroker && !crossBrokerLoading && (
                <>
                  <Button
                    size="small"
                    startIcon={<ArrowBackRounded fontSize="small" />}
                    onClick={() => setCrossBroker(null)}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Hide pivot
                  </Button>
                  <CrossPivotPanel panel={toCrossPivotPanel(crossBroker.panel)} currencySymbol={currencySymbol} />
                </>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Select a broker from the list to see its chain and root-cause analysis.
            </Typography>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
